import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';

interface HeatmapProps {
  // matrix[dow 0=Sun .. 6=Sat][hour 0..23] = count
  matrix: number[][];
  max?: number;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const HOUR_TICKS = [0, 6, 12, 18, 23] as const;

export function Heatmap({ matrix, max: maxProp }: HeatmapProps): JSX.Element {
  const max = useMemo(
    () =>
      maxProp ??
      matrix.reduce((m, row) => row.reduce((rm, v) => Math.max(rm, v), m), 0),
    [matrix, maxProp],
  );
  const [hover, setHover] = useState<{ dow: number; hour: number } | null>(
    null,
  );

  function tone(v: number): string {
    if (max === 0) return 'rgba(255,255,255,0.04)';
    const t = v / max;
    if (t === 0) return 'rgba(255,255,255,0.04)';
    // Brand red, opacity scaled non-linearly so small values are still visible.
    const opacity = 0.15 + 0.85 * Math.pow(t, 0.7);
    return `rgba(255, 0, 52, ${opacity.toFixed(3)})`;
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        className="grid"
        style={{
          gridTemplateColumns: '40px repeat(24, minmax(0, 1fr))',
          gap: 2,
        }}
        onMouseLeave={() => setHover(null)}
      >
        {/* top hour label row */}
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div
            key={`h-${h}`}
            className={cn(
              'text-[10px] text-fg-subtle text-center tabular-nums',
              HOUR_TICKS.includes(h as 0 | 6 | 12 | 18 | 23)
                ? 'visible'
                : 'invisible',
            )}
          >
            {h}
          </div>
        ))}

        {/* one row per day-of-week */}
        {DAYS.map((label, dow) => (
          <RowFragment
            key={label}
            label={label}
            row={matrix[dow] ?? []}
            dow={dow}
            tone={tone}
            onHover={(hour) => setHover({ dow, hour })}
            hovered={hover}
          />
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-fg-subtle">
        <span>UTC · hour 0–23</span>
        <div className="flex items-center gap-1.5">
          <span>0</span>
          <div
            className="h-2 w-32 rounded-full"
            style={{
              background:
                'linear-gradient(to right, rgba(255,255,255,0.04), rgba(255, 0, 52, 1))',
            }}
          />
          <span>{max.toLocaleString()}</span>
        </div>
        <span>
          {hover
            ? `${DAYS[hover.dow]} ${pad(hover.hour)}:00 — ${(
                matrix[hover.dow]?.[hover.hour] ?? 0
              ).toLocaleString()} events`
            : 'Hover for details'}
        </span>
      </div>
    </div>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function RowFragment({
  label,
  row,
  dow,
  tone,
  onHover,
  hovered,
}: {
  label: string;
  row: number[];
  dow: number;
  tone: (v: number) => string;
  onHover: (hour: number) => void;
  hovered: { dow: number; hour: number } | null;
}): JSX.Element {
  return (
    <>
      <div className="text-xs text-fg-muted self-center pr-1 text-right">
        {label}
      </div>
      {Array.from({ length: 24 }, (_, h) => {
        const v = row[h] ?? 0;
        const isHovered = hovered?.dow === dow && hovered.hour === h;
        return (
          <div
            key={h}
            onMouseEnter={() => onHover(h)}
            title={`${label} ${pad(h)}:00 — ${v.toLocaleString()} events`}
            className={cn(
              'h-6 rounded-sm cursor-pointer transition-transform',
              isHovered && 'ring-1 ring-fg/70 scale-[1.05]',
            )}
            style={{ background: tone(v) }}
          />
        );
      })}
    </>
  );
}
